import type { AddressLookupDetailsDto } from "@/api/types";
import { AddressLookupSelect } from "@/components/AddressLookupSelect";
import { ProvinceSelect } from "@/pages/Indigenous/Portal/ProvinceSelect";
import { provinceFullNameById } from "@/store/types/province";
import type { ProvinceFull } from "@/store/types/province";
import { FormGroup } from "@/components/Form/FormGroup";
import { FormInput } from "@/components/Form/FormInput";
import { H3 } from "@/components/Heading";
import { translations } from "@/constants/translations";
import { useStore } from "@/store";
import type { OrderDetails } from "@/store/types/order";
import {
  Button,
  Checkbox,
  Divider,
  HStack,
  Link,
  SimpleGrid,
  Stack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { telemetry } from "@/telemetry";

interface Props {
  onSubmit: (data: OrderDetails) => unknown;
}

export const NewBillingAddressForm = observer(({ onSubmit }: Props) => {
  const [isToggled, setToggled] = useState(false);
  const lookupSearches = useRef(0);
  const { activeLanguage, isEmergentFlow, getErrorInputText } = useStore();
  const language = translations.pages["order-summary"][activeLanguage];
  const commonLanguage = translations.common["provinces-fullName"][
    activeLanguage
  ] as any;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    reset,
    setValue,
    watch,
  } = useForm<OrderDetails>({
    mode: "all",
    reValidateMode: "onChange",
  });

  React.useEffect(() => {
    register("isManualAddress");
  }, [register]);

  const handleLookupSelect = (data: AddressLookupDetailsDto) => {
    setValue("billingCity", data.city);
    setValue("billingProvince", data.province);
    setValue("billingPostalCode", data.postalCode);
    setValue("billingStreetAddress", data.text);
  };

  const isManualAddress = watch("isManualAddress");

  const countLookupSearch = () => {
    lookupSearches.current += 1;
  };

  const handleManualAddressToggle = () => {
    if (!isManualAddress)
      telemetry.trackEvent("manual_address_toggled", {
        address: "billing",
        searches_before_switching: lookupSearches.current,
      });

    setValue("isManualAddress", !isManualAddress, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const renderManualAddressToggle = () => (
    <Link
      as="button"
      type="button"
      alignSelf="flex-start"
      bg="transparent"
      border="0"
      color="gray.400"
      fontWeight="700"
      lineHeight="1.2"
      fontSize="12px"
      p="0"
      onClick={handleManualAddressToggle}
    >
      {isManualAddress
        ? language.use_address_lookup
        : language.manual_address_lookup}
    </Link>
  );

  const allowSave = isValid;
  const billingProvince = watch("billingProvince");
  const saveAddress = watch("saveBillingAddress");
  const shippingProvinceLabel =
    commonLanguage[billingProvince] || billingProvince;

  const handleCancel = () => {
    reset();
    setToggled(false);
  };

  const onFormSubmit = (data: OrderDetails, e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    onSubmit(data);
    reset();
    setToggled(false);
  };

  return (
    <Stack
      as="form"
      spacing="8"
    >
      <HStack
        onClick={() => setToggled(!isToggled)}
        cursor="pointer"
        _hover={{
          color: "primary-light",
        }}
      >
        {isToggled ? <FiChevronDown size="28" /> : <FiChevronRight size="28" />}
        <H3>{language.enter_new_billing_info}</H3>
      </HStack>

      {isToggled && (
        <Stack
          spacing="8"
          pl="6"
        >
          <Stack
            bg="#383838"
            borderWidth="1px"
            borderColor="#DCDDDE"
            borderRadius="3px"
            p="8"
            gap="8"
          >
            <SimpleGrid
              columns={{ sm: 1, md: 2 }}
              spacing="6"
            >
              <FormInput
                label={language.shipping_name}
                error={errors.billingName}
                {...register("billingName", {
                  required: true,
                })}
              />
              <FormInput
                label={language.shipping_business_name}
                {...register("billingBusinessName", {
                  required: true,
                })}
              />
            </SimpleGrid>

            <Stack spacing="2">
              {isManualAddress ? (
                <FormInput
                  label={language.shipping_street_address}
                  error={errors.billingStreetAddress}
                  {...register("billingStreetAddress", {
                    required: getErrorInputText(
                      language.shipping_street_address,
                    ),
                  })}
                />
              ) : (
                <FormGroup
                  label={language.shipping_street_address}
                  error={errors.billingStreetAddress}
                >
                  <Controller
                    name="billingStreetAddress"
                    control={control}
                    render={({ field: { onBlur, onChange, value } }) => (
                      <AddressLookupSelect
                        name="billingStreetAddress"
                        onSearch={countLookupSearch}
                        onSelect={(data) => {
                          handleLookupSelect(data);
                          onChange(data.text);
                        }}
                        onBlur={onBlur}
                        value={value}
                        placeholder={language.shipping_street_address}
                      />
                    )}
                    rules={{
                      required: getErrorInputText(
                        language.shipping_street_address,
                      ),
                    }}
                  />
                </FormGroup>
              )}
              {renderManualAddressToggle()}
            </Stack>

            <SimpleGrid
              columns={{ sm: 1, md: 2 }}
              spacing="6"
            >
              <FormInput
                label={language.shipping_unit_suit}
                {...register("billingOptionalUnit")}
              />
              <FormInput
                label={language.shipping_city}
                isReadOnly={!isManualAddress}
                {...register("billingCity")}
              />
              {isManualAddress ? (
                <FormGroup
                  label={
                    isEmergentFlow
                      ? language.province_territory
                      : language.province
                  }
                  error={errors.billingProvince}
                >
                  <Controller
                    name="billingProvince"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ProvinceSelect
                        items={Object.values(provinceFullNameById)}
                        value={value}
                        onSelect={(v) => onChange(v as ProvinceFull)}
                        placeholder={
                          isEmergentFlow
                            ? language.province_territory
                            : language.province
                        }
                      />
                    )}
                    rules={{ required: true }}
                  />
                </FormGroup>
              ) : (
                <FormInput
                  label={
                    isEmergentFlow
                      ? language.province_territory
                      : language.province
                  }
                  isReadOnly
                  value={shippingProvinceLabel}
                />
              )}
              <FormInput
                label={language.shipping_postal_code}
                isReadOnly={!isManualAddress}
                {...register("billingPostalCode")}
              />
              <HStack
                width="100%"
                justifyContent="space-between"
                alignItems="flex-end"
              >
                <Checkbox
                  isChecked={saveAddress}
                  onChange={() => setValue("saveBillingAddress", !saveAddress)}
                >
                  {language.save_address}
                </Checkbox>
                <HStack spacing="1">
                  <Button
                    variant="text"
                    size="sm"
                    onClick={handleCancel}
                  >
                    {language.cancel_btn}
                  </Button>
                  <Divider
                    height="20px"
                    orientation="vertical"
                  />
                  <Button
                    variant="text"
                    color="primary-light"
                    size="sm"
                    onClick={handleSubmit(onFormSubmit)}
                    isDisabled={!allowSave}
                  >
                    {language.submit_btn}
                  </Button>
                </HStack>
              </HStack>
            </SimpleGrid>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});
